import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { salesApi, usersApi } from '../../services/api';
import { useAuth } from '../../store/authContext';
import { PlusIcon, SearchIcon, TrashIcon } from '../../utils/icons';
import { formatDate, formatCurrency } from '../../utils/formatters';
import Modal from '../../components/Modal';
import './SaleList.css';

const STAGES = [
  { value: 'All', label: 'All' },
  { value: 'pending', label: 'Pending' },
  { value: 'approved', label: 'Approved' },
  { value: 'eligible_for_invoice', label: 'Eligible for Invoice' },
  { value: 'invoice_raised', label: 'Invoice Raised' },
  { value: 'settled', label: 'Settled' },
];

const STAGE_LABELS = {
  pending: 'Pending',
  approved: 'Approved',
  eligible_for_invoice: 'Eligible for Invoice',
  invoice_raised: 'Invoice Raised',
  settled: 'Settled',
};

export default function SaleList() {
  const { user: currentUser } = useAuth();
  const navigate = useNavigate();
  const [sales, setSales] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [search, setSearch] = useState('');
  const [stageFilter, setStageFilter] = useState('All');
  const [agentFilter, setAgentFilter] = useState('');
  const [sort, setSort] = useState({ key: 'created_at', dir: 'desc' });
  const [deletingSale, setDeletingSale] = useState(null);

  const fetchSales = useCallback(async () => {
    try {
      setError('');
      const params = {};
      if (stageFilter !== 'All') params.stage = stageFilter;
      if (agentFilter) params.agent_id = agentFilter;
      if (search.trim()) params.search = search.trim();
      const res = await salesApi.list(params);
      setSales(res.data);
    } catch {
      setError('Failed to load sales.');
    } finally {
      setLoading(false);
    }
  }, [stageFilter, agentFilter, search]);

  const fetchUsers = useCallback(async () => {
    if (currentUser?.role !== 'admin') return;
    try {
      const res = await usersApi.list();
      setUsers(res.data);
    } catch {
      // Non-critical
    }
  }, [currentUser?.role]);

  useEffect(() => { fetchSales(); }, [fetchSales]);
  useEffect(() => { fetchUsers(); }, [fetchUsers]);

  const sorted = useMemo(() => {
    const data = [...sales];
    data.sort((a, b) => {
      let av = a[sort.key], bv = b[sort.key];
      if (sort.key === 'created_at' || sort.key === 'booking_date') { av = new Date(av); bv = new Date(bv); }
      if (av < bv) return sort.dir === 'asc' ? -1 : 1;
      if (av > bv) return sort.dir === 'asc' ? 1 : -1;
      return 0;
    });
    return data;
  }, [sales, sort]);

  const toggleSort = (key) => {
    setSort((s) => s.key === key ? { key, dir: s.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: 'desc' });
  };

  const canDelete = (sale) => {
    if (currentUser?.role === 'admin') return true;
    return sale.stage === 'pending' && sale.agent_id === currentUser?.id;
  };

  const handleDeleted = () => { setDeletingSale(null); fetchSales(); };

  if (loading) {
    return (
      <div className="sl-page">
        <div className="sl-loading">
          <div className="sl-loading-spinner" />
          <p>Loading sales...</p>
        </div>
      </div>
    );
  }

  const activeAgents = users.filter(u => u.is_active && u.assigned_modules?.includes('sales'));

  return (
    <div className="sl-page">
      {/* Breadcrumb */}
      <div className="breadcrumb animate-fade-up">
        <Link to="/sales" className="breadcrumb-link">Sales Dashboard</Link>
        <span className="breadcrumb-sep">/</span>
        <span className="breadcrumb-current">All Sales</span>
      </div>

      {/* Header */}
      <div className="page-header animate-fade-up">
        <div className="page-header-top">
          <div>
            <h1 className="page-title">Sales</h1>
            <p className="page-subtitle">{sorted.length} sale{sorted.length !== 1 ? 's' : ''}</p>
          </div>
          <div className="page-actions">
            <button className="btn btn-primary btn-sm" onClick={() => navigate('/sales/new')}>
              <PlusIcon /> New Sale
            </button>
          </div>
        </div>
      </div>

      {error && <div className="sl-error animate-fade-up">{error}</div>}

      {/* Filters */}
      <div className="sl-filters animate-fade-up" style={{ animationDelay: '60ms' }}>
        <div className="sl-filter-row">
          <div className="sl-search-wrap">
            <span className="sl-search-icon"><SearchIcon /></span>
            <input
              className="input sl-search"
              placeholder="Search by client name, PAN, project..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            {search && (
              <button className="sl-search-clear" onClick={() => setSearch('')}>
                <XIcon />
              </button>
            )}
          </div>
          {currentUser?.role === 'admin' && activeAgents.length > 0 && (
            <select
              className="input sl-agent-filter"
              value={agentFilter}
              onChange={(e) => setAgentFilter(e.target.value)}
            >
              <option value="">All Agents</option>
              {activeAgents.map(u => (
                <option key={u.id} value={u.id}>{u.name}</option>
              ))}
            </select>
          )}
        </div>

        <div className="sl-filter-chips">
          {STAGES.map((s) => (
            <button
              key={s.value}
              className={`filter-chip ${stageFilter === s.value ? 'filter-chip--active' : ''}`}
              onClick={() => setStageFilter(s.value)}
            >
              {s.label}
            </button>
          ))}
        </div>
      </div>

      {/* Table */}
      <div className="card sl-table-card animate-fade-up" style={{ animationDelay: '100ms', padding: 0 }}>
        <div className="sl-table-wrap">
          <table className="sl-table">
            <thead>
              <tr>
                <SortTh label="Client" sortKey="client_name" sort={sort} onSort={toggleSort} />
                <th>PAN</th>
                <th>Unit</th>
                <th>Project</th>
                <SortTh label="Sales Value" sortKey="total_sales_value" sort={sort} onSort={toggleSort} />
                <th>Brokerage Earned</th>
                {currentUser?.role === 'admin' && <th>Company Share</th>}
                <th>Stage</th>
                <th>Agent</th>
                <SortTh label="Booking Date" sortKey="booking_date" sort={sort} onSort={toggleSort} />
                <SortTh label="Added" sortKey="created_at" sort={sort} onSort={toggleSort} />
                <th className="sl-th-actions" />
              </tr>
            </thead>
            <tbody>
              {sorted.length === 0 ? (
                <tr>
                  <td colSpan={currentUser?.role === 'admin' ? 12 : 11} className="sl-empty">
                    <div className="sl-empty-content">
                      <EmptyIcon />
                      <p>No sales match your filters</p>
                      <button
                        className="btn btn-ghost btn-sm"
                        onClick={() => { setSearch(''); setStageFilter('All'); setAgentFilter(''); }}
                      >
                        Clear filters
                      </button>
                    </div>
                  </td>
                </tr>
              ) : (
                sorted.map((sale) => (
                  <tr key={sale.id}>
                    <td>
                      <Link to={`/sales/${sale.id}`} className="sl-name-link">
                        <div className="sl-avatar-sm">
                          {sale.client_name.split(' ').map(n => n[0]).slice(0, 2).join('')}
                        </div>
                        <span className="sl-name">{sale.client_name}</span>
                      </Link>
                    </td>
                    <td className="sl-mono">{sale.pan_number}</td>
                    <td><span className="sl-unit-badge">{sale.unit_type}</span></td>
                    <td className="sl-project">{sale.project_name || '—'}</td>
                    <td className="sl-money">{formatCurrency(sale.total_sales_value)}</td>
                    <td className="sl-money">{formatCurrency(sale.brokerage_earned)}</td>
                    {currentUser?.role === 'admin' && (
                      <td className="sl-money sl-company">{formatCurrency(sale.company_share)}</td>
                    )}
                    <td><SaleStageBadge stage={sale.stage} /></td>
                    <td>
                      <span className="sl-agent-pill">{sale.agent_name || 'Agent'}</span>
                    </td>
                    <td className="sl-time">{sale.booking_date ? formatDate(sale.booking_date) : '—'}</td>
                    <td className="sl-time">{formatDate(sale.created_at)}</td>
                    <td className="sl-td-actions">
                      <div className="sl-row-actions">
                        <Link to={`/sales/${sale.id}`} className="btn-icon" title="View">
                          <EyeIcon />
                        </Link>
                        {canDelete(sale) && (
                          <button
                            className="btn-icon"
                            title="Delete"
                            onClick={(e) => { e.stopPropagation(); setDeletingSale(sale); }}
                          >
                            <TrashIcon />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <div className="sl-table-footer">
          <p className="sl-table-count">{sorted.length} sale{sorted.length !== 1 ? 's' : ''}</p>
        </div>
      </div>

      {/* Delete confirmation */}
      {deletingSale && (
        <DeleteSaleModal
          sale={deletingSale}
          onClose={() => setDeletingSale(null)}
          onDeleted={handleDeleted}
        />
      )}
    </div>
  );
}


/* ─── Delete Confirmation Modal ─────────────────────────────────────────── */

function DeleteSaleModal({ sale, onClose, onDeleted }) {
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState('');

  const handleConfirm = async () => {
    setDeleting(true);
    setError('');
    try {
      await salesApi.delete(sale.id);
      onDeleted();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to delete sale.');
      setDeleting(false);
    }
  };

  return (
    <Modal title="Delete Sale" size="sm" onClose={onClose}>
      <div className="modal-body">
        {error && <div className="modal-error">{error}</div>}
        <p className="delete-msg">
          Are you sure you want to delete the sale for <strong>{sale.client_name}</strong>?
        </p>
        <p className="delete-hint">
          This sale will be archived and no longer visible in the sales list.
        </p>
      </div>
      <div className="modal-footer">
        <button type="button" className="btn btn-ghost" onClick={onClose}>Cancel</button>
        <button type="button" className="btn btn-danger" onClick={handleConfirm} disabled={deleting}>
          {deleting ? 'Deleting...' : 'Delete Sale'}
        </button>
      </div>
    </Modal>
  );
}


/* ─── Sub-components ─────────────────────────────────────────────────────── */

function SortTh({ label, sortKey, sort, onSort }) {
  const active = sort.key === sortKey;
  return (
    <th className={`sl-th-sort ${active ? 'sl-th-sort--active' : ''}`} onClick={() => onSort(sortKey)}>
      {label}
      <span className="sl-sort-icon">{active ? (sort.dir === 'asc' ? '\u2191' : '\u2193') : '\u2195'}</span>
    </th>
  );
}

export function SaleStageBadge({ stage }) {
  const cls = stage || 'default';
  return (
    <span className={`badge badge-sale-${cls}`}>
      <span className="badge-dot" />
      {STAGE_LABELS[stage] || stage}
    </span>
  );
}


/* ─── Page-specific icons ───────────────────────────────────────────────── */

function XIcon() {
  return <svg width="12" height="12" viewBox="0 0 16 16" fill="none"><path d="M3 3l10 10M13 3L3 13" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/></svg>;
}
function EyeIcon() {
  return <svg width="13" height="13" viewBox="0 0 16 16" fill="none"><path d="M1 8s2.5-5 7-5 7 5 7 5-2.5 5-7 5-7-5-7-5z" stroke="currentColor" strokeWidth="1.4"/><circle cx="8" cy="8" r="2" stroke="currentColor" strokeWidth="1.4"/></svg>;
}
function EmptyIcon() {
  return <svg width="36" height="36" viewBox="0 0 40 40" fill="none"><rect x="4" y="8" width="32" height="26" rx="3" stroke="currentColor" strokeWidth="1.5" opacity="0.3"/><path d="M12 18h16M12 24h10" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" opacity="0.3"/><circle cx="30" cy="30" r="7" fill="var(--bg-base)" stroke="currentColor" strokeWidth="1.5" opacity="0.5"/><path d="M27 30h6M30 27v6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" opacity="0.5"/></svg>;
}
